/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  currencyFilterStatus,
} from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import CurrencyFilterList from './List';

/**
 * Query to fetch currency filters from api
 */
export const CURRENCY_FILTERS_QUERY = gql`
  query CurrencyFilterList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $status: [FilterCurrencyFilterStatus]
    $currency: [FilterCurrencyFilterCurrency]
    $from: DateTime
    $to: DateTime
  ) {
    currencyFilters(
      first: $first
      after: $after
      last: $last
      before: $before
      status: $status
      currency: $currency
      fromDate: $from
      toDate: $to
    ) @connection(key: "currencyFilters") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          status
          createdOn
          createdBy {
            id
            fullname
          }
          updatedOn
          updatedBy {
            id
            fullname
          }
          disabledOn
          currency
          rate
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Default filters in CurrencyFilterList
 */
export const defaultFilters = {
  status: [currencyFilterStatus.ENABLED, currencyFilterStatus.DISABLED],
};

/**
 * @class
 * @extends Component
 */
class CurrencyFilterListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      query: this.listCurrencyFilter,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listCurrencyFilter = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={CURRENCY_FILTERS_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const {
      pageInfo,
      edges: newEdges,
    } = options.fetchMoreResult.currencyFilters;
    if (newEdges.length !== 0) {
      return {
        currencyFilters: {
          __typename: previousResult.currencyFilters.__typename,
          edges: [...previousResult.currencyFilters.edges, ...newEdges],
          pageInfo: {
            ...previousResult.currencyFilters.pageInfo,
            hasNextPage: pageInfo.hasNextPage,
          },
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: {
        ...filters,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render CurrencyFilterList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      failedIds,
      pagination,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({ query: { loading, data } }) => (
          <CurrencyFilterList
            data={data}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
            restartFilters={this.handleRestartFilters}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * CurrencyFilterListContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

CurrencyFilterListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default CurrencyFilterListContainer;
